import React, { useState, useEffect } from 'react';
import { calendrierAPI } from '../services/api';
import { departementCourant, niveauModule, peutAccederAdministration, NIVEAU_MASQUE } from '../services/droits';
import './AppSidebar.css';

// ----------------------------------------------------------------
// Palette charte graphique QLIO Manager
// color       = couleur icône sidebar (vive, sur fond sombre)
// topbarColor = couleur topbar (profonde, fond blanc → texte blanc dessus)
// Pour ajouter un module : choisir couleurs non utilisées dans les deux palettes
// Voir memory/project_charte_graphique.md
// ----------------------------------------------------------------
// Aligné sur doc/maquettes/maquette-sidebar.js (référence validée) : 18 modules
// + Administration, catégorie « Pilotage », couleurs de la charte régénérée.
// `dev: true` = module spécifié mais pas encore développé (badge « dev », non cliquable).
export const NAV_STRUCTURE = [
    { cat: 'Tableau de bord', items: [
        { key: 'accueil',  icon: 'fa-house',         label: 'Accueil',         color: '#60a5fa', topbarColor: '#2563eb', dev: false },
        { key: 'planning', icon: 'fa-calendar-days', label: 'Planning annuel', color: '#60a5fa', topbarColor: '#2563eb', dev: false },
        { key: 'store',    icon: 'fa-store',         label: 'Store',           color: '#10b981', topbarColor: '#047857', dev: true },
    ]},
    { cat: 'Soutenances', items: [
        { key: 'soutenance', icon: 'fa-microphone-lines', label: 'Planning oraux',     color: '#fb923c', topbarColor: '#ea580c', dev: false },
        { key: 'jury',       icon: 'fa-users-line',       label: 'Jury & évaluations', color: '#fb923c', topbarColor: '#ea580c', dev: true },
    ]},
    { cat: 'Étudiants', items: [
        { key: 'etudiants', icon: 'fa-user-graduate',        label: 'Étudiants',                 color: '#f472b6', topbarColor: '#db2777', dev: false },
        { key: 'stage',     icon: 'fa-building',             label: 'Suivi stages / alternance', color: '#4ade80', topbarColor: '#16a34a', dev: false },
        { key: 'poursuite', icon: 'fa-graduation-cap',       label: "Poursuite d'études",        color: '#a3e635', topbarColor: '#65a30d', dev: true },
        { key: 'voeux',     icon: 'fa-square-poll-vertical', label: 'Enquêtes & Vœux',           color: '#2dd4bf', topbarColor: '#0d9488', dev: true },
    ]},
    { cat: 'Ressources', items: [
        { key: 'enseignants', icon: 'fa-chalkboard-user', label: 'Enseignants',         color: '#a78bfa', topbarColor: '#7c3aed', dev: false },
        { key: 'salles',      icon: 'fa-door-open',       label: 'Réservation salles',  color: '#a78bfa', topbarColor: '#7c3aed', dev: true },
        { key: 'programme',   icon: 'fa-book-open',       label: 'Programme formation', color: '#818cf8', topbarColor: '#4338ca', dev: false },
    ]},
    { cat: 'Analyses', items: [
        { key: 'kpi', icon: 'fa-chart-pie', label: 'Analyses & KPI', color: '#c084fc', topbarColor: '#9333ea', dev: true },
    ]},
    { cat: 'Planification', items: [
        { key: 'edt', icon: 'fa-table-cells', label: 'EDT Builder', color: '#22d3ee', topbarColor: '#0891b2', dev: false },
    ]},
    { cat: 'Pilotage', items: [
        { key: 'vie-departement', icon: 'fa-landmark',     label: 'Vie du département',        color: '#e11d48', topbarColor: '#9f1239', dev: true },
        { key: 'communication',   icon: 'fa-envelope',     label: 'Communication',             color: '#e879f9', topbarColor: '#c026d3', dev: true },
        { key: 'charges',         icon: 'fa-chart-column', label: 'Service & responsabilités', color: '#fbbf24', topbarColor: '#ca8a04', dev: true },
        { key: 'projets',         icon: 'fa-industry',     label: 'Projets & investissements', color: '#d97706', topbarColor: '#b45309', dev: true },
    ]},
    { cat: 'Gestion', adminOnly: true, items: [
        { key: 'admin', icon: 'fa-gear', label: 'Administration', color: '#94a3b8', topbarColor: '#475569', dev: false, badge: { label: 'Admin', cls: 'gray' } },
    ]},
];

// Couleur icône sidebar (vive, pour fond sombre)
export function getModuleColor(key) {
    for (const section of NAV_STRUCTURE) {
        const item = section.items.find(i => i.key === key);
        if (item) return item.color;
    }
    return '#60a5fa';
}

// Couleur topbar (profonde, pour gradient sur fond blanc → texte blanc)
export function getModuleTopbarColor(key) {
    for (const section of NAV_STRUCTURE) {
        const item = section.items.find(i => i.key === key);
        if (item) return item.topbarColor;
    }
    return '#2563eb';
}

function getInitiales(prenom, nom) {
    const p = (prenom || '').trim()[0] || '';
    const n = (nom    || '').trim()[0] || '';
    return (p + n).toUpperCase() || '?';
}

// ----------------------------------------------------------------
// Props :
//   activeKey       — clé du module actif
//   user, config, onNavigate, onLogout
//   annees          — optionnel : si fourni, utilisé tel quel
//   anneeId         — optionnel
//   onAnneeChange   — optionnel
//   filterKeys      — optionnel : si fourni, n'affiche que les items dont la clé est dans ce tableau
//   (si annees non fourni, la sidebar charge les années elle-même)
// ----------------------------------------------------------------
function AppSidebar({ activeKey, user, config, onNavigate, onLogout, annees, anneeId, onAnneeChange, filterKeys }) {
    const [collapsed, setCollapsed] = useState(false);
    const [localAnnees, setLocalAnnees] = useState([]);
    const [localAnneeId, setLocalAnneeId] = useState(null);

    // Entrée « Administration » : super-administrateur OU administrateur de département
    const isAdmin = peutAccederAdministration(user);
    const deptId = departementCourant(user, config);
    const deptNom = config?.departement?.nom || config?.departement?.titre_planning || 'Département';

    // Auto-chargement si annees pas fournies en props
    useEffect(() => {
        if (annees === undefined) {
            calendrierAPI.getAllVisible()
                .then(res => {
                    const liste = res.data?.annees || [];
                    setLocalAnnees(liste);
                    if (liste.length > 0) {
                        const active = liste.find(a => a.active);
                        if (active) setLocalAnneeId(active.id);
                    }
                })
                .catch(() => {});
        }
    }, []);

    const displayAnnees   = annees !== undefined ? annees : localAnnees;
    const displayAnneeId  = anneeId !== undefined ? anneeId : localAnneeId;
    const handleAnneeChange = (id) => {
        if (onAnneeChange) onAnneeChange(id);
        else setLocalAnneeId(id);
    };

    return (
        <aside className={`app-sidebar ${collapsed ? 'app-sidebar-collapsed' : ''}`}>

            <button
                className="app-sb-toggle"
                onClick={() => setCollapsed(c => !c)}
                title={collapsed ? 'Agrandir' : 'Réduire'}
            >
                <i className={`fa-solid ${collapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`} />
            </button>

            {/* Logo */}
            <div className="app-sb-logo">
                <div className="app-sb-logo-icon">
                    <i className="fa-solid fa-graduation-cap" />
                </div>
                {!collapsed && (
                    <div className="app-sb-logo-text">
                        <div className="app-sb-logo-name">
                            {config?.departement?.nom ? `${config.departement.nom} Manager` : 'Département Manager'}
                        </div>
                        <div className="app-sb-logo-sub">Gestion département</div>
                    </div>
                )}
            </div>

            {/* Formation active — toujours présent sur toutes les pages */}
            {collapsed ? (
                <div className="app-sb-formation-mini" title="Année universitaire en cours">
                    {displayAnnees.find(a => a.id === displayAnneeId)?.libelle
                        ?.replace(/20(\d{2})-20(\d{2})/, '$1-$2') || '—'}
                </div>
            ) : (
                <div className="app-sb-formation">
                    <label>Année universitaire</label>
                    <select
                        value={displayAnneeId || ''}
                        onChange={e => handleAnneeChange(parseInt(e.target.value))}
                    >
                        {displayAnnees && displayAnnees.length > 0
                            ? displayAnnees.map(a => (
                                <option key={a.id} value={a.id}>{a.libelle}</option>
                            ))
                            : <option value={config?.anneeScolaireId || ''}>Année en cours</option>
                        }
                    </select>
                </div>
            )}

            {/* Navigation */}
            <nav className="app-sb-nav">
                {NAV_STRUCTURE.map(section => {
                    if (section.adminOnly && !isAdmin) return null;
                    let visibleItems = filterKeys
                        ? section.items.filter(i => filterKeys.includes(i.key))
                        : section.items;
                    // Droits : un module « masqué » disparaît de la navigation
                    // (le refus réel reste côté serveur, cf. services/droits.js)
                    if (!section.adminOnly) {
                        // Lot 3.5c-4 (E159) : un compte LIÉ à une fiche enseignant voit Enseignants (« Mon espace »)
                        // même sans droit sur le module — sinon un vacataire n'atteint pas ses visites.
                        visibleItems = visibleItems.filter(
                            i => niveauModule(user, deptId, i.key) !== NIVEAU_MASQUE
                                 || (i.key === 'enseignants' && !!user?.enseignant_id)
                        );
                    }
                    if (visibleItems.length === 0) return null;
                    return (
                        <div key={section.cat}>
                            {!collapsed && !filterKeys && <div className="app-nav-cat">{section.cat}</div>}
                            {visibleItems.map(item => {
                                const isActive   = item.key === activeKey;
                                const isDisabled = item.dev;
                                const itemPath = item.key === 'accueil' ? '/' : `/${item.key}`;
                                return (
                                    <a
                                        key={item.key}
                                        href={itemPath}
                                        className={`app-nav-item ${isActive ? 'app-nav-active' : ''} ${isDisabled ? 'app-nav-disabled' : ''}`}
                                        onClick={(e) => { e.preventDefault(); if (!isDisabled) onNavigate(item.key); }}
                                        title={item.label}
                                        style={{ textDecoration: 'none', color: 'inherit', ...(isActive ? { borderLeftColor: item.color } : {}) }}
                                    >
                                        <i
                                            className={`fa-solid ${item.icon}`}
                                            style={{ color: !isDisabled ? item.color : undefined }}
                                        />
                                        {!collapsed && (
                                            <>
                                                <span className="app-nav-label">{item.label}</span>
                                                {isActive && (
                                                    <span className="app-nav-badge" style={{ background: item.color }}>Actif</span>
                                                )}
                                                {isDisabled && (
                                                    <span className="app-nav-badge app-badge-dev">dev</span>
                                                )}
                                                {item.badge && !isDisabled && !isActive && (
                                                    <span className={`app-nav-badge app-badge-${item.badge.cls}`}>{item.badge.label}</span>
                                                )}
                                            </>
                                        )}
                                    </a>
                                );
                            })}
                        </div>
                    );
                })}
            </nav>

            {/* Copyright */}
            {!collapsed && (
                <div className="app-sb-copyright">
                    © {new Date().getFullYear()} DÉPARTEMENT MANAGER
                </div>
            )}

            {/* Utilisateur */}
            <div className="app-sb-user" onClick={onLogout} title="Se déconnecter">
                <div className="app-sb-avatar">{getInitiales(user?.prenom, user?.nom)}</div>
                {!collapsed && (
                    <>
                        <div>
                            <div className="app-sb-user-name">{user?.prenom} {user?.nom}</div>
                            <div className="app-sb-user-role">{user?.role_nom}</div>
                        </div>
                        <i className="fa fa-arrow-right-from-bracket app-sb-user-chevron" />
                    </>
                )}
            </div>

        </aside>
    );
}

export default AppSidebar;
